import { forwardRef, useId } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import type { InputHTMLAttributes } from 'react';

export interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
    /** Field label text */
    label: string;
    /** Error message to display */
    error?: string;
    /** Whether the field is required */
    required?: boolean;
    /** Helper text shown below the input */
    helperText?: string;
    /** Custom ID (auto-generated if not provided) */
    id?: string;
    /** Additional wrapper class */
    wrapperClassName?: string;
}

/**
 * FormField Component
 *
 * A complete form field with label, input, and error handling.
 * Automatically handles accessibility (aria-invalid, aria-describedby).
 *
 * @example
 * <FormField
 *   label="Email"
 *   type="email"
 *   required
 *   error={errors.email?.message}
 *   {...register('email')}
 * />
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
    ({
        label,
        error,
        required = false,
        helperText,
        id: customId,
        wrapperClassName,
        className,
        ...inputProps
    }, ref) => {
        const generatedId = useId();
        const id = customId || generatedId;
        const errorId = `${id}-error`;
        const helperId = `${id}-helper`;

        // Build aria-describedby from error and helper
        const describedBy = [
            error ? errorId : null,
            helperText ? helperId : null,
        ].filter(Boolean).join(' ') || undefined;

        return (
            <div className={cn('space-y-1.5', wrapperClassName)}>
                <label
                    htmlFor={id}
                    className="block text-sm font-medium leading-6 text-foreground"
                >
                    {label}
                    {required && (
                        <>
                            <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                            <span className="sr-only">(required)</span>
                        </>
                    )}
                </label>

                <Input
                    ref={ref}
                    id={id}
                    aria-required={required}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={describedBy}
                    className={cn(
                        'min-h-[48px]',
                        error && 'border-destructive focus-visible:ring-destructive',
                        className
                    )}
                    {...inputProps}
                />

                {error && (
                    <p
                        id={errorId}
                        className="text-sm text-destructive flex items-center gap-1.5"
                        role="alert"
                    >
                        {error}
                    </p>
                )}

                {helperText && !error && (
                    <p
                        id={helperId}
                        className="text-sm text-muted-foreground"
                    >
                        {helperText}
                    </p>
                )}
            </div>
        );
    }
);

FormField.displayName = 'FormField';

export default FormField;
